import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const loadRazorpaySdk = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const PayButton = ({ planId, planName, featured }) => {
  const [loading, setLoading] = useState(false);

  const startCheckout = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const sdkReady = await loadRazorpaySdk();
      if (!sdkReady) throw new Error('sdk-load-failed');
      const { data } = await axios.post(`${API}/payments/create-order`, { plan_id: planId });
      const rzp = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: data.amount,
        currency: data.currency,
        name: 'CareerWithRaaj',
        description: planName,
        order_id: data.order_id,
        theme: { color: '#0A192F' },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/payments/verify`, { ...resp, plan_id: planId });
            toast.success(`Payment successful! We'll reach out to schedule your ${planName}.`);
          } catch {
            toast.error('Payment went through but verification failed. Please contact us with your payment ID.');
          }
        },
        modal: { ondismiss: () => toast.info('Payment cancelled — no charge was made.') },
      });
      rzp.on('payment.failed', (resp) => {
        toast.error(`Payment failed: ${resp?.error?.description || 'please try again'}`);
      });
      rzp.open();
    } catch {
      toast.error('Could not start the payment. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={startCheckout}
      disabled={loading}
      data-testid={`plan-book-cta-${planName.toLowerCase().replace(/\s+/g, '-')}`}
      className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold transition-[background-color,color,transform] duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 ${
        featured
          ? 'bg-coral text-white hover:bg-coral-deep'
          : 'border-2 border-navy/15 text-navy hover:border-coral hover:text-coral'
      }`}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {loading ? 'Opening secure checkout…' : 'Book Now'}
    </button>
  );
};

export default PayButton;
